<script lang="ts">
import Vue, { CreateElement } from 'vue';
import { Component, Prop, Provide, Ref } from 'vue-property-decorator';
import Swiper, { Autoplay, SwiperOptions } from 'swiper';
import GftSwiperThumbs from './GftSwiperThumbs.vue';
import { getTwConfig } from './utils';

@Component({
    components: { GftSwiperThumbs }
})
class GftSwiper extends Vue {
    @Ref('container') container;

    @Prop({ default: 'horizontal' }) direction!: 'horizontal' | 'vertical';
    // diferent direction if touch device
    @Prop({ default: '' }) touchDirection!: 'horizontal' | 'vertical';
    @Prop({ type: Boolean, default: false }) contrast: boolean;
    @Prop({ type: Boolean, default: false }) loop!: boolean;
    @Prop({ type: Boolean, default: false }) autoplay!: boolean;
    @Prop({ default: 0 }) gap: number | { [breakpoint: string]: number };
    @Prop({ default: '' }) containerClasses: string;
    @Prop({ default: '' }) slideClasses: string;
    @Prop({ default: '' }) wrapperClasses: string;
    @Prop({ default: 'div' }) slideTag: string;
    @Prop({ default: 'div' }) wrapperTag: string;
    @Prop({ default: 0 }) start: number;
    @Prop({ default: 300 }) speed: number;
    @Prop({ default: 0 }) slidesOffsetAfter!: number | { [breakpoint: string]: number };
    @Prop({ type: Boolean, default: false }) showThumbs: boolean;
    @Prop({ type: Boolean, default: false }) fullHeightSlides: boolean;
    @Prop({ type: Boolean, default: false }) static: boolean;
    @Prop({ type: Boolean, default: false }) editMode: boolean;
    @Prop({ default: 'auto' }) slidesPerView: number | 'auto' | { [breakpoint: string]: number | 'auto' };
    @Provide('registerSlide') childRegistration = this.register;

    slides = [];
    swiper = null;
    index = 0;
    options: SwiperOptions = {
        watchOverflow: true,
        observer: true,
        loop: this.loop,
        speed: 300,
        autoplay: this.autoplay ? { delay: 5000, disableOnInteraction: false } : false,
        direction: this.direction,
        touchReleaseOnEdges: true,
        allowSlidePrev: false,
        initialSlide: 0,
        observeParents: true,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        slideToClickedSlide: true,
        init: false
    };

    // aux for direction in touchDevices
    touchDevice = false;

    twConfig = getTwConfig();

    mounted() {
        this.index = this.start;
        this.options.speed = this.speed;
        this.options.initialSlide = this.index;
        this.options.loop = this.loop;
        this.options.autoplay = this.autoplay ? { delay: 5000, disableOnInteraction: false } : false;
        this.options.direction = this.direction;
        this.$nextTick(() => {
            this.touchDevice = ('ontouchstart' in document.documentElement);
            Swiper.use([Autoplay]);
            if (this.editMode) {
                return;
            }
            // check device and set direction
            if (this.touchDirection && this.touchDevice) {
                this.setBreakpointOption(this.touchDirection, 'direction');
            } else {
                this.setBreakpointOption(this.direction, 'direction');
            }
            this.setBreakpointOption(this.gap, 'spaceBetween');
            this.setBreakpointOption(this.slidesPerView, 'slidesPerView');
            this.setBreakpointOption(this.slidesOffsetAfter, 'slidesOffsetAfter');
            this.swiper = new Swiper(this.container, this.options);
            this.swiper.on('activeIndexChange', this.indexChanged);
            this.swiper.on('reachEnd', () => this.$emit('end-reached'));
            this.swiper.init();
        });
    }

    indexChanged({ activeIndex, realIndex }) {
        this.index = this.loop ? realIndex : activeIndex;
        this.$emit('index-changed', this.index);
    }

    next() {
        if (this.swiper) {
            if (this.loop) {
                this.index = this.index + 1 >= this.slides.length ? 0 : this.index + 1;
            } else if (this.index + 1 < this.swiper.slides.length) {
                this.index++;
            }
            this.swiper.slideTo(this.index);
        }
    }

    prev() {
        if (this.swiper) {
            if (this.loop) {
                this.index = this.index > 0 ? this.index - 1 : this.swiper.slides.length - 1;
            } else if (this.index > 0) {
                this.index--;
            }
            this.swiper.slideTo(this.index);
        }
    }

    goTo(index) {
        if (this.swiper) {
            this.swiper.slideTo(index);
            this.index = index;
        }
    }

    render(h: CreateElement) {
        return h('div', { staticClass: 'w-full h-full' }, [
            h('div', {
                staticClass: ['swiper-container h-full w-full overflow-hidden', this.containerClasses].filter(x => x.length).join(' '),
                ref: 'container'
            }, [
                h(this.wrapperTag, {
                    staticClass: ['swiper-wrapper flex flex-nowrap h-full', this._wrapperClasses, this.wrapperClasses].filter(x => x.length).join(' ')
                }, this.$slots.default ? this.$slots.default.filter(x => x.tag).map((el, i) => h(this.slideTag, {
                    staticClass: `swiper-slide shrink-0 max-w-full ${this.slideClasses}${this.index === i ? ' slide-active' : ''}`
                }, [el])) : [])
            ]),
            (this.showThumbs && this.slides.length) ? h('gft-swiper-thumbs', {
                props: {
                    slides: this.slides.map(({ kicker, title, text, image }) => ({ kicker, title, text, image })),
                    index: this.index,
                    contrast: this.contrast
                },
                on: {
                    thumb: this.goTo
                }
            }) : null,
            (this.$scopedSlots.pagination && !this.editMode) ? this.$scopedSlots.pagination({
                index: this.swiper ? this.swiper.realIndex + 1 : 0,
                total: this.numSlides,
                teaser: this.currentSlide,
                contrast: this.currentSlide && this.currentSlide.contrast ? this.currentSlide.contrast : false,
                go: this.goTo,
                uid: this._uid
            }) : null,
            (this.$scopedSlots.controls) ? this.$scopedSlots.controls({
                prev: this.prev,
                next: this.next,
                total: this.numSlides,
                index: this.index
            }) : null
        ]);
    }

    // called by child slides
    register(slide) {
        slide.$on('click', e => {
            const index = this.slides.findIndex(x => x._id === e._id);
            if (index >= 0 && index < this.slides.length) {
                this.goTo(index);
            }
        });
        this.slides.push(slide);
    }

    breakpointSize(name: string): number {
        if (Object.prototype.hasOwnProperty.call(this.twConfig.theme.screens, name)) {
            return parseInt(this.twConfig.theme.screens[name]);
        }
        return -1;
    }

    setBreakpointOption(value, name) {
        const breakpoints = this.options.breakpoints || {};
        if (typeof value === 'number' || typeof value === 'string') {
            this.options[name] = value;
            return;
        }
        Object.entries(value).forEach(x => {
            const bp = this.breakpointSize(x[0]);
            if (bp > 0) {
                if (!Object.prototype.hasOwnProperty.call(breakpoints, bp)) {
                    breakpoints[bp] = {};
                }
                breakpoints[bp][name] = x[1];
            } else {
                this.options[name] = x[1];
            }
        });
        if (!this.options.breakpoints) {
            this.options.breakpoints = breakpoints;
        }
    }

    // check children to only count slide components
    filterSlides(child: Vue) {
        return child.$vnode && child.$vnode.componentOptions && child.$vnode.componentOptions.tag === 'gft-swiper-slide';
    }

    get numSlides() {
        if (this.$slots.default) {
            return this.$slots.default.filter(x => x.tag).length;
        }
        return 0;
    }

    get currentSlide() {
        const index = (this.index - this.start) < 0 ? this.slides.length - 1 : this.index - this.start;
        if (this.slides.length && this.slides.length > index) {
            return this.slides[index];
        }
        return null;
    }

    // set class with direction
    // set for diferent breakpoints and check if have diferent direction in touch devices
    get _wrapperClasses() {
        if ((this.touchDirection && this.touchDevice && typeof this.touchDirection === 'string') || (!(this.touchDirection && this.touchDevice) && typeof this.direction === 'string')) {
            return this.direction === 'vertical' ? 'flex-col' : 'flex-row';
        }
        // md:flex-col lg:flex-col xl:flex-col md:flex-row lg:flex-row xl:flex-row
        const classes = [];
        Object.entries(this.touchDirection && this.touchDevice ? this.touchDirection : this.direction).forEach(x => {
            const bp = this.breakpointSize(x[0]);
            const dir = x[1] === 'vertical' ? 'flex-col' : 'flex-row';
            if (bp > 0) {
                classes.push(`${x[0]}:${dir}`);
            } else {
                classes.push(dir);
            }
        });

        return classes.join(' ');
    }
}

export default Vue.component('gft-swiper', GftSwiper);

</script>
